/**
 * "Switch to Monthly Delivery" — converts a one-time line to a SKIO subscription.
 *
 * /cart/change.js CANNOT set a selling plan on an existing line, so conversion is
 * remove + re-add (or remove + qty merge onto an existing subscription line).
 *
 * The Welcome Kit glass jar is a separate $0 OTP line and is deliberately NOT
 * touched — it must not recur and must not be re-added.
 */

/**
 * @param {string} url
 * @param {object} body
 * @returns {Promise<object>}
 */
async function postJSON(url, body) {
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`${url} failed: ${res.status}`);
  return res.json();
}

/**
 * @param {string} routeName
 * @param {string} fallback
 * @returns {string}
 */
function getThemeRoute(routeName, fallback) {
  const theme = window.Theme;

  if (theme && theme.routes && theme.routes[routeName]) {
    return theme.routes[routeName];
  }

  return fallback;
}

/**
 * Collects section IDs from every cart-items-component on the page.
 * @returns {string}
 */
function getCartSectionIds() {
  const sections = new Set();
  document.querySelectorAll('cart-items-component').forEach((item) => {
    if (item instanceof HTMLElement && item.dataset.sectionId) {
      sections.add(item.dataset.sectionId);
    }
  });
  return Array.from(sections).join(',');
}

/**
 * Dispatches the theme cart:update event so drawers/icons morph.
 * @param {object} cart
 * @param {object} [sections]
 */
function dispatchCartUpdate(cart, sections) {
  document.dispatchEvent(
    new CustomEvent('cart:update', {
      bubbles: true,
      detail: {
        resource: cart,
        sourceId: 'cart-switch-to-subscription',
        data: {
          itemCount: cart.item_count,
          source: 'cart-switch-to-subscription',
          sections: sections || {},
        },
      },
    }),
  );
}

/**
 * @param {string} template
 * @param {number} quantity
 * @returns {string}
 */
function scaleIncluded(template, quantity) {
  if (!template || quantity <= 0) return template || '';
  return template.replace(/(\d+)x/g, (_, n) => `${parseInt(n, 10) * quantity}x`);
}

/**
 * Find an existing subscription line for the same variant + selling plan.
 * @param {object[]} items
 * @param {number} variantId
 * @param {number} sellingPlanId
 * @param {string} excludeKey
 * @returns {object|null}
 */
function findSubscriptionMergeTarget(items, variantId, sellingPlanId, excludeKey) {
  for (const item of items) {
    if (!item || item.key === excludeKey) continue;
    if (Number(item.variant_id) !== Number(variantId)) continue;

    const plan = item.selling_plan_allocation && item.selling_plan_allocation.selling_plan;
    if (!plan) continue;
    if (Number(plan.id) !== Number(sellingPlanId)) continue;

    return item;
  }

  return null;
}

/**
 * @param {HTMLElement|null} cartItem
 * @param {object|null} line
 * @returns {string}
 */
function getIncludesTemplate(cartItem, line) {
  if (cartItem && cartItem.dataset && cartItem.dataset.includesTemplate) {
    return cartItem.dataset.includesTemplate.trim();
  }

  return '';
}

/**
 * @param {object} line
 * @param {string} includesTemplate
 * @param {number} quantity
 * @returns {object|null}
 */
function buildMergedProperties(line, includesTemplate, quantity) {
  const existing = line && line.properties && typeof line.properties === 'object' ? line.properties : {};
  const properties = {};

  for (const key of Object.keys(existing)) {
    if (key === 'Includes') continue;
    const value = existing[key];
    if (value != null && value !== '') {
      properties[key] = String(value);
    }
  }

  if (includesTemplate) {
    properties.Includes = scaleIncluded(includesTemplate, quantity);
  } else if (existing.Includes) {
    properties.Includes = String(existing.Includes);
  }

  return Object.keys(properties).length ? properties : null;
}

/**
 * @param {HTMLElement} trigger
 */
function useStordSellingPlans() {
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get('plans') === 'stord') return true;
    if (params.get('plans') === 'skio') return false;
    return sessionStorage.getItem('vivvira-plans') === 'stord';
  } catch (error) {
    return false;
  }
}

async function switchLineToSubscription(trigger) {
  const lineKey = trigger.dataset.lineKey;
  const variantId = Number(trigger.dataset.variantId);
  let sellingPlanId = Number(trigger.dataset.sellingPlanId);
  if (useStordSellingPlans() && trigger.dataset.stordSellingPlanId) {
    sellingPlanId = Number(trigger.dataset.stordSellingPlanId);
  }
  const quantity = Number(trigger.dataset.quantity || 1);
  const sectionIds = getCartSectionIds() || trigger.dataset.sectionId || '';

  if (!lineKey || !variantId || !sellingPlanId) return;

  trigger.setAttribute('aria-busy', 'true');

  const cartItem = document.querySelector(`.cart-item[data-key="${lineKey}"]`);
  if (cartItem) {
    cartItem.classList.add('cart-item--loading');
  }

  let cart;
  try {
    cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
  } catch (error) {
    console.error('[cart] switch to subscription failed to load cart', error);
    trigger.removeAttribute('aria-busy');
    if (cartItem) cartItem.classList.remove('cart-item--loading');
    return;
  }

  const line = (cart.items || []).find((item) => item.key === lineKey);
  const lineQuantity = line && line.quantity ? Number(line.quantity) : quantity;
  const mergeTarget = findSubscriptionMergeTarget(cart.items || [], variantId, sellingPlanId, lineKey);
  const includesTemplate = getIncludesTemplate(cartItem, line);

  let properties = null;
  if (line && line.properties && Object.keys(line.properties).length) {
    properties = { ...line.properties };
  }

  try {
    await postJSON(getThemeRoute('cart_change_url', '/cart/change.js'), {
      id: lineKey,
      quantity: 0,
    });

    try {
      let result;

      if (mergeTarget) {
        const mergedQuantity = Number(mergeTarget.quantity) + lineQuantity;
        const mergePayload = {
          id: mergeTarget.key,
          quantity: mergedQuantity,
        };
        const mergedProperties = buildMergedProperties(mergeTarget, includesTemplate, mergedQuantity);
        if (mergedProperties) {
          mergePayload.properties = mergedProperties;
        }
        if (sectionIds) {
          mergePayload.sections = sectionIds;
          mergePayload.sections_url = window.location.pathname;
        }

        result = await postJSON(getThemeRoute('cart_change_url', '/cart/change.js'), mergePayload);
      } else {
        const payload = {
          items: [{ id: variantId, quantity: lineQuantity, selling_plan: sellingPlanId }],
        };
        if (includesTemplate) {
          payload.items[0].properties = {
            ...(properties || {}),
            Includes: scaleIncluded(includesTemplate, lineQuantity),
          };
        } else if (properties) {
          payload.items[0].properties = properties;
        }
        if (sectionIds) {
          payload.sections = sectionIds;
          payload.sections_url = window.location.pathname;
        }

        result = await postJSON(getThemeRoute('cart_add_url', '/cart/add.js'), payload);
      }

      const updatedCart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) =>
        r.json(),
      );
      dispatchCartUpdate(updatedCart, result.sections);
    } catch (addError) {
      const restore = { items: [{ id: variantId, quantity: lineQuantity }] };
      if (properties) restore.items[0].properties = properties;
      if (sectionIds) {
        restore.sections = sectionIds;
        restore.sections_url = window.location.pathname;
      }

      const restoreResult = await postJSON(getThemeRoute('cart_add_url', '/cart/add.js'), restore).catch(
        () => null,
      );
      const restoredCart = await fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then((r) => r.json())
        .catch(() => null);
      if (restoredCart) {
        const restoredSections = restoreResult && restoreResult.sections ? restoreResult.sections : {};
        dispatchCartUpdate(restoredCart, restoredSections);
      }
      throw addError;
    }
  } catch (error) {
    console.error('[cart] switch to subscription failed', error);
    trigger.removeAttribute('aria-busy');
    if (cartItem) {
      cartItem.classList.remove('cart-item--loading');
    }
  }
}

document.addEventListener('click', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;
  const trigger = target.closest('[data-switch-to-subscription]');
  if (!(trigger instanceof HTMLElement)) return;
  event.preventDefault();
  switchLineToSubscription(trigger);
});
